/**
 * Excused-absence quota display rules. Pure data-in/data-out — no React, no
 * i18n — so AttendanceRow and the workbench/session-detail call sites read
 * the same rule instead of re-deriving it. See
 * .superpowers/sdd/2026-08-15-excused-absence-quota/ for the design spec.
 */

interface QuotaParticipant {
  excusedThisMonth: number;
  madeUpThisMonth: number;
  chargeAbsent: boolean;
  pricingType: string;
}

/**
 * Whether the monthly excused-absence cap applies to this enrollment at all:
 * only PER_SESSION enrollments with chargeAbsent on and an org-configured
 * limit are subject to it. MONTHLY_FLAT billing is unaffected by absence
 * count, and a null limit means "no cap configured".
 */
function quotaApplies(p: QuotaParticipant, limit: number | null): limit is number {
  return p.chargeAbsent && p.pricingType === 'PER_SESSION' && limit !== null;
}

/**
 * Badge text next to the "Có phép" label, whether the enrollment is at or
 * past its monthly cap, and how many made-up absences to advertise.
 *
 * All rules key off the *net* count — `net = max(0, excusedThisMonth -
 * madeUpThisMonth)` — not the raw `excusedThisMonth`: an excused absence that
 * was made up in another slot stops consuming quota (see
 * docs/superpowers/specs/2026-08-19-quota-makeup-netting-design.md). When the
 * quota applies, the badge is always "net/limit" (even at 0, so the cap stays
 * visible) and overLimit flips at net >= limit. When it doesn't apply, the
 * badge is a plain net count that disappears entirely at 0 (nothing worth
 * mentioning), and overLimit is always false.
 *
 * `madeUpShown` is the transparency fragment for the caller to compose (this
 * lib stays translation-free): non-null only when there is both a make-up
 * (`madeUpThisMonth > 0`) and something it made up for
 * (`excusedThisMonth > 0`), clamped to `min(madeUpThisMonth,
 * excusedThisMonth)` so the note never advertises more refund than there
 * were absences. `madeUpThisMonth: 0` always yields `madeUpShown: null` and a
 * badge identical to the pre-netting behaviour.
 */
export function excusedQuotaDisplay(
  p: QuotaParticipant,
  limit: number | null,
): { badge: string | null; overLimit: boolean; madeUpShown: number | null } {
  const net = Math.max(0, p.excusedThisMonth - p.madeUpThisMonth);
  const madeUpShown =
    p.madeUpThisMonth > 0 && p.excusedThisMonth > 0 ? Math.min(p.madeUpThisMonth, p.excusedThisMonth) : null;

  if (quotaApplies(p, limit)) {
    return { badge: `${net}/${limit}`, overLimit: net >= limit, madeUpShown };
  }
  // Even a net-to-zero count must still show "0" once a make-up is being
  // advertised — otherwise "Có phép (đã bù 2)" renders with no badge at all,
  // silently dropping the count the parenthetical refers to (see design doc
  // 2026-08-22-states §3).
  return { badge: net > 0 || madeUpShown != null ? `${net}` : null, overLimit: false, madeUpShown };
}

interface ChargeNoteParticipant {
  chargeAbsent: boolean;
  pricingType: string;
  billingPeriod: string;
}

/**
 * i18n key for the note shown under an ABSENT row. MONTHLY_FLAT wins over
 * chargeAbsent — a flat enrollment's absence never bills regardless of that
 * flag's value — so it is checked first.
 */
export function absenceChargeNoteKey(
  p: ChargeNoteParticipant,
): 'chargedMonthly' | 'chargedTerm' | 'notCharged' | 'flat' {
  if (p.pricingType === 'MONTHLY_FLAT') return 'flat';
  if (!p.chargeAbsent) return 'notCharged';
  return p.billingPeriod === 'TERM' ? 'chargedTerm' : 'chargedMonthly';
}

/** Which sentence pair `attendance.quotaPopover.*` should render. */
export type QuotaExplanationKey = 'withLimit' | 'withLimitMadeUp' | 'noLimit' | 'noLimitMadeUp';

export interface QuotaExplanation {
  key: QuotaExplanationKey;
  /**
   * The NET count — the same numerator `excusedQuotaDisplay` puts in the badge.
   * Deliberately not the raw `excusedThisMonth`: the popover exists to explain
   * the badge, and a sentence whose number disagrees with the chip it hangs
   * off explains nothing. The made-up clause is what accounts for the gap.
   */
  used: number;
  /** null when no cap applies (MONTHLY_FLAT, chargeAbsent off, or no org limit). */
  limit: number | null;
  /** Clamped make-ups, or null when there is no make-up worth mentioning. */
  madeUp: number | null;
}

/**
 * The sentence behind the quota chip (04 Bàn làm việc): "Tháng này {{name}}
 * nghỉ có phép {{used}} buổi trong hạn mức {{limit}}. {{madeUp}} buổi đã học bù
 * nên được hoàn phép." — with the limit clause dropped when no cap applies and
 * the make-up clause dropped when there was none.
 *
 * Returns a KEY plus params, never a string: this lib stays translation-free,
 * the same way `absenceChargeNoteKey` does.
 *
 * Returns **null in exactly the cases `excusedQuotaDisplay` returns
 * `badge: null`** — nothing to show, so nothing to explain. Callers gate the
 * chip on this one value, which is what keeps a chip from ever appearing with
 * no explanation behind it, or vice versa.
 */
export function quotaExplanation(p: QuotaParticipant, limit: number | null): QuotaExplanation | null {
  const { badge, madeUpShown } = excusedQuotaDisplay(p, limit);
  if (badge === null) return null;
  const used = Math.max(0, p.excusedThisMonth - p.madeUpThisMonth);
  const capped = quotaApplies(p, limit);
  const key: QuotaExplanationKey = capped
    ? madeUpShown != null
      ? 'withLimitMadeUp'
      : 'withLimit'
    : madeUpShown != null
      ? 'noLimitMadeUp'
      : 'noLimit';
  return { key, used, limit: capped ? limit : null, madeUp: madeUpShown };
}
